import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface BoxBlurParams {
  radius: number;
}

// ---------------------------------------------------------------------------
// Premultiply / unpremultiply
// ---------------------------------------------------------------------------

function premultiply(src: Uint8ClampedArray, w: number, h: number): Float32Array {
  const out = new Float32Array(w * h * 4);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3] ?? 0;
    const af = a / 255;
    out[o] = (src[o] ?? 0) * af;
    out[o + 1] = (src[o + 1] ?? 0) * af;
    out[o + 2] = (src[o + 2] ?? 0) * af;
    out[o + 3] = a;
  }
  return out;
}

function unpremultiply(
  src: Float32Array,
  w: number,
  h: number,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(w * h * 4);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3] ?? 0;
    if (a <= 0) {
      out[o] = 0;
      out[o + 1] = 0;
      out[o + 2] = 0;
      out[o + 3] = 0;
      continue;
    }
    const inv = 255 / a;
    out[o] = Math.round((src[o] ?? 0) * inv);
    out[o + 1] = Math.round((src[o + 1] ?? 0) * inv);
    out[o + 2] = Math.round((src[o + 2] ?? 0) * inv);
    out[o + 3] = Math.round(a);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Separable box blur passes (sliding window, O(n) per pass)
// ---------------------------------------------------------------------------

function blurH(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  radius: number,
): void {
  const winSize = 2 * radius + 1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let sumR = 0;
    let sumG = 0;
    let sumB = 0;
    let sumA = 0;

    // Prime the window.
    for (let k = -radius; k <= radius; k++) {
      let sx = k;
      if (sx < 0) sx = 0;
      else if (sx >= w) sx = w - 1;
      const o = (row + sx) * 4;
      sumR += src[o] ?? 0;
      sumG += src[o + 1] ?? 0;
      sumB += src[o + 2] ?? 0;
      sumA += src[o + 3] ?? 0;
    }

    for (let x = 0; x < w; x++) {
      const o = (row + x) * 4;
      const inv = 1 / winSize;
      dst[o] = sumR * inv;
      dst[o + 1] = sumG * inv;
      dst[o + 2] = sumB * inv;
      dst[o + 3] = sumA * inv;

      // Slide window: subtract outgoing pixel, add incoming pixel.
      let outX = x - radius;
      if (outX < 0) outX = 0;
      else if (outX >= w) outX = w - 1;
      let inX = x + radius + 1;
      if (inX < 0) inX = 0;
      else if (inX >= w) inX = w - 1;

      const oOut = (row + outX) * 4;
      const oIn = (row + inX) * 4;
      sumR += (src[oIn] ?? 0) - (src[oOut] ?? 0);
      sumG += (src[oIn + 1] ?? 0) - (src[oOut + 1] ?? 0);
      sumB += (src[oIn + 2] ?? 0) - (src[oOut + 2] ?? 0);
      sumA += (src[oIn + 3] ?? 0) - (src[oOut + 3] ?? 0);
    }
  }
}

function blurV(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  radius: number,
): void {
  const winSize = 2 * radius + 1;
  for (let x = 0; x < w; x++) {
    let sumR = 0;
    let sumG = 0;
    let sumB = 0;
    let sumA = 0;

    // Prime the window.
    for (let k = -radius; k <= radius; k++) {
      let sy = k;
      if (sy < 0) sy = 0;
      else if (sy >= h) sy = h - 1;
      const o = (sy * w + x) * 4;
      sumR += src[o] ?? 0;
      sumG += src[o + 1] ?? 0;
      sumB += src[o + 2] ?? 0;
      sumA += src[o + 3] ?? 0;
    }

    for (let y = 0; y < h; y++) {
      const o = (y * w + x) * 4;
      const inv = 1 / winSize;
      dst[o] = sumR * inv;
      dst[o + 1] = sumG * inv;
      dst[o + 2] = sumB * inv;
      dst[o + 3] = sumA * inv;

      // Slide window: subtract outgoing pixel, add incoming pixel.
      let outY = y - radius;
      if (outY < 0) outY = 0;
      else if (outY >= h) outY = h - 1;
      let inY = y + radius + 1;
      if (inY < 0) inY = 0;
      else if (inY >= h) inY = h - 1;

      const oOut = (outY * w + x) * 4;
      const oIn = (inY * w + x) * 4;
      sumR += (src[oIn] ?? 0) - (src[oOut] ?? 0);
      sumG += (src[oIn + 1] ?? 0) - (src[oOut + 1] ?? 0);
      sumB += (src[oIn + 2] ?? 0) - (src[oOut + 2] ?? 0);
      sumA += (src[oIn + 3] ?? 0) - (src[oOut + 3] ?? 0);
    }
  }
}

// ---------------------------------------------------------------------------
// Filter export
// ---------------------------------------------------------------------------

export const BoxBlur: Filter<BoxBlurParams> = {
  id: "box-blur",
  displayName: "Box Blur",
  defaultParams: { radius: 10 },

  apply(
    input: PixelBuffer,
    params: BoxBlurParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);

    const radius = Math.round(params.radius);
    if (!(radius > 0) || w === 0 || h === 0) {
      out.data.set(input.data);
      return out;
    }

    const pre = premultiply(input.data, w, h);
    const tmp = new Float32Array(w * h * 4);
    blurH(pre, tmp, w, h, radius);
    blurV(tmp, pre, w, h, radius);
    const result = unpremultiply(pre, w, h);
    out.data.set(result);
    return out;
  },
};
